import * as React from 'react';
import { useNavigate } from 'react-router';
import { ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import { ContactSupport, EditNoteRounded, Feedback as FeedbackIcon, JoinInner, Person, Restore } from '@mui/icons-material';

import { DialogContext } from '@cvt/contexts';
import { useRouter } from '@cvt/routing';
import { useDictionary } from '@cvt/hooks/useDictionary';

import { UserContext } from '@modules/Users/contexts';
import { useUserCrud } from '@modules/Users/hooks/useUserCrud';

import config from '@shared/config';
import { useLeaveReview } from '@shared/hooks/useLeaveReview';
import { ShareMenuItem } from '@shared/components/ShareMenuItem';

type Props = {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  anchorOrigin?: React.ComponentProps<typeof Menu>['anchorOrigin'];
  transformOrigin?: React.ComponentProps<typeof Menu>['transformOrigin'];
};

export const ProfileMenu: React.FC<Props> = ({ anchorEl, onClose, anchorOrigin, transformOrigin }) => {
  const router = useRouter();
  const dictionary = useDictionary();
  const navigate = useNavigate();
  const { user } = React.useContext(UserContext);
  const { openDialog, asyncConfirmation } = React.useContext(DialogContext);
  const { resetAccount } = useUserCrud();
  const leaveReview = useLeaveReview();

  const go = (path: string) => {
    navigate(path);
    onClose();
  };

  const openContactUs = () => {
    openDialog('supportContact');
    onClose();
  };

  const handleLeaveReview = () => {
    leaveReview();
    onClose();
  };

  const handleResetAccount = async () => {
    onClose();
    const userConfirmed = await asyncConfirmation({
      title: 'Reset Account',
      content: 'Are you sure you want to reset your account? This action cannot be undone.',
      confirmLabel: 'Reset',
      cancelLabel: 'Cancel',
    });
    if (!userConfirmed) return;
    await resetAccount.mutateAsync();
  };

  return (
    <Menu
      anchorEl={anchorEl}
      open={!!anchorEl}
      onClose={onClose}
      anchorOrigin={anchorOrigin}
      transformOrigin={transformOrigin}
      slotProps={{
        paper: {
          elevation: 0,
          sx: { borderRadius: 0, border: 1, borderColor: 'divider' },
        },
      }}
    >
      <MenuItem onClick={() => go(router.profile.path)}>
        <ListItemIcon><Person fontSize="small" /></ListItemIcon>
        <ListItemText>{dictionary.menu.users.myAccount}</ListItemText>
      </MenuItem>
      {user?.ongoingFeedbackSession && (
        <MenuItem onClick={() => go(router.feedback.path)}>
          <ListItemIcon><FeedbackIcon fontSize="small" /></ListItemIcon>
          <ListItemText>{dictionary.menu.feedback}</ListItemText>
        </MenuItem>
      )}
      {config.featureFlags.understandMatches && (
        <MenuItem onClick={() => go(router.understandMatches.path)}>
          <ListItemIcon><JoinInner fontSize="small" /></ListItemIcon>
          <ListItemText>{dictionary.menu.understandMatchesShort}</ListItemText>
        </MenuItem>
      )}
      <MenuItem onClick={handleLeaveReview}>
        <ListItemIcon><EditNoteRounded fontSize="small" /></ListItemIcon>
        <ListItemText>{dictionary.menu.leaveReview}</ListItemText>
      </MenuItem>
      {user?.archetype && (
        <ShareMenuItem
          archetype={user.archetype}
          archetypeDescription={user.archetypeDescription}
          label={dictionary.menu.share}
          onAfterShare={onClose}
        />
      )}
      <MenuItem onClick={openContactUs}>
        <ListItemIcon><ContactSupport fontSize="small" /></ListItemIcon>
        <ListItemText>{dictionary.menu.contactUs}</ListItemText>
      </MenuItem>
      <MenuItem onClick={handleResetAccount}>
        <ListItemIcon><Restore fontSize="small" /></ListItemIcon>
        <ListItemText>{dictionary.menu.resetAccount}</ListItemText>
      </MenuItem>
    </Menu>
  );
};
